import {html} from 'lit';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type {CustomSelectEvent} from '#lit/components/base/select/index.js';
import {SelectVariant} from '#lit/components/base/select/index.js';
import type {DateInputString, HourAndMinuteString} from '#lit/types/general.js';
import type {
  Computed,
  ComputedModel,
} from '#lit/components/mixins/immutable_component/computed.js';
import {createModel} from '#lit/components/mixins/immutable_component/computed.js';
import type {Timezone} from '#lit/types/timezone.js';
import {
  getBrowserTimezone,
  getTimezone,
} from '#lit/common/constants/timezones.js';
import type {CustomInputEvent} from '#lit/components/base/input/index.js';
import {Variant as InputVariant} from '#lit/components/base/input/index.js';
import {dispatchEvents, validateSchedule} from './utils.js';
import type FlyScheduleDateInput from './index.js';

// -- Component dependencies
import '#lit/components/base/list_item/index.js';
import {isDateInputString, isHourAndMinuteString} from '#lit/utils/dates.js';
import {exportActivitiesLocale} from '#lit/common/locale/export_activities.js';
import {Fill} from '../../icon/index.js';

export type Model = {
  form: string;
  name: string;
  label: string;
  scheduled: boolean;
  date: DateInputString | '';
  time: HourAndMinuteString | '';
  timezone: Timezone;
  hidden: boolean;
  disabled: boolean;
  required: boolean;
  scheduleOptions: Array<{label: string; value: boolean}>;
  valid: Computed<boolean>;
};

export type CModel = ComputedModel<Model>;

export const Msg = makeVariants({
  setLabel: Variant<string>()('set-label'),
  setScheduled: Variant<boolean>()('set-scheduled'),
  setDate: Variant<DateInputString | ''>()('set-date'),
  setTime: Variant<HourAndMinuteString | ''>()('set-time'),
  setTimezone: Variant<string>()('set-timezone'),
  setHidden: Variant<boolean>()('set-hidden'),
  setDisabled: Variant<boolean>()('set-disabled'),
  setRequired: Variant<boolean>()('set-required'),
});

export type Msg = VariantsFor<typeof Msg>;

enum ExportScheduleType {
  START,
  END,
}

const exportScheduleLabels: Record<ExportScheduleType, string> = {
  [ExportScheduleType.START]: 'Start',
  [ExportScheduleType.END]: 'End',
};

// -- Init

function init(props: InitialProperties, _cmd: Cmd, _model?: CModel) {
  const form = <string | undefined>props.get('form') ?? '';
  const name = <string | undefined>props.get('name') ?? '';
  const label = <string | undefined>props.get('label') ?? '';
  const scheduled = <boolean | undefined>props.get('scheduled') ?? false;
  const date = <DateInputString | undefined>props.get('date') ?? ('' as const);
  const time =
    <HourAndMinuteString | undefined>props.get('time') ?? ('' as const);
  const timezoneName = <string | undefined>props.get('timezone');
  const browserTimezone = getBrowserTimezone();
  const timezone = timezoneName
    ? (getTimezone(timezoneName) ?? browserTimezone)
    : browserTimezone;
  const hidden = <boolean | undefined>props.get('hidden') ?? false;
  const disabled = <boolean | undefined>props.get('disabled') ?? false;
  const required = <boolean | undefined>props.get('required') ?? false;
  const optionLabel = <string | undefined>props.get('noScheduleLabel') ?? 'Now';
  const scheduleOptions = [
    {label: optionLabel, value: false},
    {label: 'Schedule', value: true},
  ];

  const updatedModel = createModel<Model>(compute => ({
    form,
    name,
    label,
    scheduled,
    date,
    time,
    timezone,
    hidden,
    disabled,
    required,
    scheduleOptions,
    valid: compute(model => validateSchedule(model)),
  }));

  return updatedModel;
}

// -- Update

function update(msg: Msg, model: CModel, cmd: Cmd): ImmutableResult<CModel> {
  switch (msg.tag) {
    case 'set-scheduled':
      if (model.scheduled === msg.value) return model;
      return [
        {...model, scheduled: msg.value},
        cmd((host: FlyScheduleDateInput) => dispatchEvents(host, 'scheduled')),
      ];

    case 'set-date':
      if (model.date === msg.value) return model;
      return [
        {...model, date: msg.value},
        cmd((host: FlyScheduleDateInput) => dispatchEvents(host, 'date')),
      ];

    case 'set-time':
      if (model.time === msg.value) return model;

      return [
        {...model, time: msg.value},
        cmd((host: FlyScheduleDateInput) => dispatchEvents(host, 'time')),
      ];

    case 'set-timezone': {
      if (model.timezone.name === msg.value) return model;
      if (
        msg.value === '' &&
        model.timezone?.name === getBrowserTimezone().name
      ) {
        return model;
      }
      return [
        {...model, timezone: getTimezone(msg.value) ?? getBrowserTimezone()},
        cmd((host: FlyScheduleDateInput) => dispatchEvents(host, 'timezone')),
      ];
    }

    case 'set-label':
      return {...model, label: msg.value};

    case 'set-disabled':
      return {...model, disabled: msg.value};

    case 'set-hidden':
      return {...model, hidden: msg.value};

    case 'set-required': {
      return {...model, required: msg.value};
    }
  }
}

function renderLabel(label: string) {
  if (label === exportScheduleLabels[ExportScheduleType.START]) {
    return html`
      <fly-tooltip
        content=${exportActivitiesLocale.en.audienceExports.nowSelected}
      >
        <div class="inline-fieldset--label">
          ${label}
          <fly-icon fill=${Fill.BASE} icon="info_outline"></fly-icon>
        </div>
      </fly-tooltip>
    `;
  }
  return label;
}

// -- View

function view(model: CModel, handleUpdate: UpdateHandler<Model, Msg>) {
  return html`
    <fieldset class="inline-fieldset">
      <div class="controls">
        <label>
          <slot name="label"> ${renderLabel(model.label)} </slot>
        </label>

        <fly-select
          ?disabled=${model.disabled}
          selectionRequired
          variant=${SelectVariant.CONDENSED_ROUNDED_SQUARE}
          initial=${model.scheduled}
          @selected=${(e: CustomEvent<CustomSelectEvent<'true' | 'false'>>) => {
            handleUpdate(Msg.setScheduled(e.detail.value === 'true'));
          }}
        >
          ${repeat(
            model.scheduleOptions,
            option => option.value,
            option => html`
              <fly-list-item
                value=${String(option.value)}
                ?selected=${option.value === model.scheduled}
                ?inert=${option.value === model.scheduled}
              >
                ${option.label}
              </fly-list-item>
            `
          )}
        </fly-select>

        ${when(model.scheduled, () => scheduleFields(model, handleUpdate))}
      </div>
    </fieldset>
  `;
}

function scheduleFields(
  model: CModel,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  return html`
    <div class="inline-fields">
      <label>
        Date
        <fly-input
          type="date"
          ?disabled=${model.disabled}
          variant=${InputVariant.OUTLINED}
          value=${model.date}
          @change=${(e: CustomEvent<CustomInputEvent>) => {
            const date = e.detail.value;
            if (isDateInputString(date)) {
              handleUpdate(Msg.setDate(date));
            }
          }}
        ></fly-input>
      </label>

      <label>
        Time
        <fly-input
          type="time"
          ?disabled=${model.disabled}
          variant=${InputVariant.OUTLINED}
          value=${model.time}
          @change=${(e: CustomEvent<CustomInputEvent>) => {
            const time = e.detail.value;
            if (isHourAndMinuteString(time)) {
              handleUpdate(Msg.setTime(time));
            }
          }}
        ></fly-input>
      </label>

      <label>
        Timezone
        <fly-timezone-select
          id="timezone-input"
          ?disabled=${model.disabled}
          value=${model.timezone?.name ?? ''}
          @selected=${(e: CustomEvent<{timezone: Timezone}>) => {
            handleUpdate(Msg.setTimezone(e.detail.timezone.name));
          }}
        ></fly-timezone-select>
      </label>
    </div>
  `;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
